import React from 'react';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import Packages from '../components/Packages';
import ContactCTA from '../components/ContactCTA';
import SEO from '../components/SEO';
import { combineSchemas, organizationSchema, serviceSchema, breadcrumbSchema } from '../lib/schema';
import { Check, Layers, Code2, ShoppingCart, RefreshCw } from 'lucide-react';

const features = [
  { icon: Layers, title: 'Custom UI/UX', text: 'Tailored, on-brand interfaces designed to convert visitors into customers.' },
  { icon: Code2, title: 'Modern Tech Stack', text: 'React, Next.js, WordPress, Shopify — chosen to fit your business needs.' },
  { icon: ShoppingCart, title: 'eCommerce Ready', text: 'Secure payment gateways, inventory management & a smooth checkout flow.' },
  { icon: RefreshCw, title: 'Ongoing Support', text: 'Edits, updates, maintenance and analytics support after launch.' },
];

const WebDesignPage = () => (
  <Layout>
    <SEO
      title="Custom Web Design & Development Services | Website Symphony"
      description="Custom web design and development for businesses of every size. WordPress, Shopify, custom React & Next.js builds. Conversion-focused, mobile-first, SEO-ready. Free quote."
      keywords="custom web design, web development, WordPress design, Shopify design, React web development, business web design, small business website"
      canonical="/web-design"
      schema={combineSchemas(
        organizationSchema,
        serviceSchema('Custom Web Design & Development', 'Custom web design and development services including WordPress, Shopify, custom React and eCommerce builds.', '/web-design'),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Web Design', path: '/web-design' }]),
      )}
    />
    <PageHero
      title="Web Design & Development"
      subtitle="Custom websites engineered to convert — from brand new launches to complete redesigns."
      breadcrumb="Web Design"
    />

    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6 text-center mb-14">
        <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-5">Built for performance. Designed for people.</h2>
        <p className="text-gray-600 max-w-3xl mx-auto text-[15px] leading-relaxed">
          Every Website Symphony project begins with strategy: who your customer is, what they want, and how your site should lead them to action. We then design, develop, test and launch a site that performs on every device.
        </p>
      </div>
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {features.map((f) => (
          <div key={f.title} className="bg-[#F7F7F5] rounded-lg p-7 hover:shadow-lg transition-shadow">
            <div className="w-14 h-14 rounded-full bg-[#2C5F66]/10 flex items-center justify-center mb-5">
              <f.icon className="text-[#2C5F66]" size={26} />
            </div>
            <h3 className="text-[#2C5F66] text-lg font-bold mb-2">{f.title}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{f.text}</p>
          </div>
        ))}
      </div>
    </section>

    <Packages />

    <section className="py-20 bg-[#F7F7F5]">
      <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <h3 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-6">Our Web Design Process</h3>
          <ol className="space-y-4">
            {['Discovery & Strategy', 'Wireframes & Visual Design', 'Development & CMS Setup', 'Testing & QA', 'Launch & Ongoing Support'].map((step, i) => (
              <li key={step} className="flex items-start gap-4">
                <span className="w-9 h-9 rounded-full bg-[#C68B30] text-white font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <div>
                  <h4 className="text-[#2C5F66] font-semibold">{step}</h4>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div>
          <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=900&q=80" alt="process" className="rounded-lg shadow-xl w-full h-[420px] object-cover" />
        </div>
      </div>
    </section>

    <ContactCTA defaultService="Web Design" heading="Ready to start your project?" subheading="Get a free, no-obligation quote tailored to your business goals." />
  </Layout>
);

export default WebDesignPage;
